import type { OutboxRecord, OutboxStore } from './store';

/**
 * A ~60-line IndexedDB wrapper, generalized out of the check-in module's original
 * `outbox/idb.ts` (WS-13) so a second outbox (recipe photos, WS-20) can open its own
 * database/store names through the same four-method contract, instead of forking this
 * file. Still just `Blob`s of a few hundred KB, stored durably without base64-inflating
 * them by a third — the one thing `localStorage` can't do.
 */

function request<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB request failed'));
  });
}

/** Opens (creating on first use) an IndexedDB database with one object store per name in
 * `storeNames`, each keyed by `id`. */
export function openIdbOutboxDb(
  factory: IDBFactory,
  dbName: string,
  version: number,
  storeNames: readonly string[],
): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const open = factory.open(dbName, version);
    open.onupgradeneeded = () => {
      const db = open.result;
      for (const name of storeNames) {
        if (!db.objectStoreNames.contains(name)) db.createObjectStore(name, { keyPath: 'id' });
      }
    };
    open.onsuccess = () => resolve(open.result);
    open.onerror = () => reject(open.error ?? new Error('IndexedDB open failed'));
  });
}

export class IdbOutboxStore<T extends OutboxRecord> implements OutboxStore<T> {
  /** The database handle is opened once and shared; every call awaits the same promise. */
  private readonly db: Promise<IDBDatabase>;

  constructor(
    private readonly storeName: string,
    db: Promise<IDBDatabase>,
  ) {
    this.db = db;
  }

  private async tx(mode: IDBTransactionMode): Promise<IDBObjectStore> {
    const db = await this.db;
    return db.transaction(this.storeName, mode).objectStore(this.storeName);
  }

  async put(record: T): Promise<void> {
    const store = await this.tx('readwrite');
    await request(store.put(record));
  }

  async get(id: string): Promise<T | undefined> {
    const store = await this.tx('readonly');
    return (await request(store.get(id))) as T | undefined;
  }

  async all(): Promise<T[]> {
    const store = await this.tx('readonly');
    return (await request(store.getAll())) as T[];
  }

  async delete(id: string): Promise<void> {
    const store = await this.tx('readwrite');
    await request(store.delete(id));
  }
}
